import { BooleanFieldModel, createEmailField, StringFieldModel } from "@/common/commonForms";
import {
  DCEvent,
  EventDispatcher,
  showAlert,
  showGeneralError,
  thunk,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { portfolioApi } from "@/portfolio/portfolioDomain";
import { MatchedDCRoute } from "@/routes/routes";
import { User } from "@/types/UserInfo";
import { getFullName } from "@/utils/Roles";

const MAX_COMPANIES_IN_WELCOME_TEXT = 5;

export type AcceptUserTermsState = {
  emailModel: StringFieldModel;
  acceptedTermsModel: BooleanFieldModel;
  isAccepting?: boolean;
};

export type AcceptUserTermsViewProps = {
  fullName: string;
  infoMessage: TMessage;
  emailModel: StringFieldModel;
  acceptedTermsModel: BooleanFieldModel;
  isAccepting: boolean;
  acceptUserTerms: () => void;
};

export const requiresUserTermsAcceptance = (
  user: User | null,
  route: MatchedDCRoute | undefined,
): boolean => user?.acceptedTerms === false && route?.data?.allowAnonymous !== true;

export const initAcceptUserTerms = (email: string | undefined): UpdateStoreEvent => ({
  type: "update-store",
  data: { acceptUserTerms: (current) => current ?? newAcceptUserTermsState(email) },
});

const newAcceptUserTermsState = (email: string | undefined): AcceptUserTermsState => ({
  emailModel: createEmailField({ required: true, value: email }),
  acceptedTermsModel: new BooleanFieldModel({ requiredTrue: true }),
});

export const prepAcceptUserTerms = ({
  state,
  user,
  companies,
  dispatch,
  onAccepted,
}: {
  state: AcceptUserTermsState | undefined;
  user: User | null;
  companies: { name: string }[];
  dispatch: EventDispatcher;
  onAccepted: (email: string) => void;
}): AcceptUserTermsViewProps | undefined =>
  state && {
    fullName: getFullName(user),
    infoMessage: welcomeInfoMessage(companies),
    emailModel: state.emailModel,
    acceptedTermsModel: state.acceptedTermsModel,
    isAccepting: state.isAccepting ?? false,
    acceptUserTerms: () => acceptUserTerms(state, dispatch, onAccepted),
  };

const welcomeInfoMessage = (companies: { name: string }[]): TMessage =>
  companies.length === 0
    ? { key: "registerUser.infoNoCompanies" }
    : {
        key: "registerUser.infoHasCompanies",
        params: { companies: companies.slice(0, MAX_COMPANIES_IN_WELCOME_TEXT).map((c) => c.name) },
      };

const acceptUserTerms = (
  state: AcceptUserTermsState,
  dispatch: EventDispatcher,
  onAccepted: (email: string) => void,
) => {
  const isTermsValid = state.acceptedTermsModel.validate();
  const isEmailValid = state.emailModel.validate();
  const email = state.emailModel.validatedValue;
  if (!isTermsValid || !isEmailValid || !email) {
    return;
  }
  dispatch([
    setAccepting(true),
    portfolioApi.acceptUserTermsAndUpdateEmail({
      data: { email },
      onSuccess: (): DCEvent[] => [
        clearAcceptUserTerms,
        thunk(() => onAccepted(email)),
        showAlert({ key: "general-success" }),
      ],
      onFailure: (): DCEvent[] => [setAccepting(undefined), showGeneralError()],
    }),
  ]);
};

const setAccepting = (isAccepting: boolean | undefined): UpdateStoreEvent => ({
  type: "update-store",
  data: { acceptUserTerms: (current) => current && { ...current, isAccepting } },
});

const clearAcceptUserTerms: UpdateStoreEvent = {
  type: "update-store",
  data: { acceptUserTerms: undefined },
};
